import { useQuery } from "@apollo/client";
import {
  Badge,
  Card,
  createStyles,
  Grid,
  Group,
  SimpleGrid,
  Text,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconCash,
  IconCategory,
  IconChecks,
  IconClock,
  IconFileDescription,
  IconUsers,
} from "@tabler/icons";
import {
  ADMIN_LOAN_CATEGORIES,
  ADMIN_LOAN_PRODUCTS,
  LOAN_ACCOUNTS,
  LOAN_APPLICATIONS,
} from "apollo/queries";
import { useState } from "react";

const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
  },

  title: {
    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
    fontWeight: 700,
  },

  item: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    borderRadius: theme.radius.md,
    height: 120,
    backgroundColor:
      theme.colorScheme === "dark"
        ? theme.colors.dark[7]
        : theme.colors.gray[0],
    transition: "box-shadow 150ms ease, transform 100ms ease",

    "&:hover": {
      boxShadow: `${theme.shadows.md} !important`,
      transform: "scale(1.05)",
    },
  },
}));

const LoanProviderDashboard = () => {
  const { classes } = useStyles();
  const [categoryCount, setCategoryCount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [pendingApplications, setPendingApplications] = useState(0);
  const [approvedApplications, setApprovedApplications] = useState(0);
  const [activeAccounts, setActiveAccounts] = useState(0);
  const [totalDisbursed, setTotalDisbursed] = useState(0);

  // Fetch loan categories count
  const { loading: categoriesLoading } = useQuery(ADMIN_LOAN_CATEGORIES, {
    variables: {
      first: 1,
      page: 1,
      search: "",
      orderBy: [{ column: "SORT_ORDER", order: "ASC" }],
    },
    onCompleted(data) {
      setCategoryCount(data?.adminLoanCategories?.paginatorInfo?.total || 0);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to fetch categories count",
      });
    },
  });

  // Fetch loan products count
  const { loading: productsLoading } = useQuery(ADMIN_LOAN_PRODUCTS, {
    variables: {
      first: 1,
      page: 1,
      search: "",
      orderBy: [{ column: "NAME", order: "ASC" }],
    },
    onCompleted(data) {
      setProductCount(data?.adminLoanProducts?.paginatorInfo?.total || 0);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to fetch products count",
      });
    },
  });

  // Fetch pending loan applications
  const { loading: pendingLoading } = useQuery(LOAN_APPLICATIONS, {
    variables: {
      workflow_status: "PENDING",
      first: 1,
      page: 1,
      orderBy: [{ column: "created_at", order: "DESC" }],
    },
    onCompleted(data) {
      setPendingApplications(data?.loanApplications?.paginatorInfo?.total || 0);
    },
    onError(error) {
      console.error("Error fetching pending applications:", error);
    },
  });

  // Fetch approved loan applications
  const { loading: approvedLoading } = useQuery(LOAN_APPLICATIONS, {
    variables: {
      status: "APPROVED",
      first: 1,
      page: 1,
      orderBy: [{ column: "created_at", order: "DESC" }],
    },
    onCompleted(data) {
      setApprovedApplications(
        data?.loanApplications?.paginatorInfo?.total || 0,
      );
    },
    onError(error) {
      console.error("Error fetching approved applications:", error);
    },
  });

  // Fetch active loan accounts
  const { loading: accountsLoading } = useQuery(LOAN_ACCOUNTS, {
    variables: {
      status: "ACTIVE",
      first: 10,
      page: 1,
      orderBy: [{ column: "created_at", order: "DESC" }],
    },
    onCompleted(data) {
      setActiveAccounts(data?.loanAccounts?.paginatorInfo?.total || 0);
      // Calculate total disbursed amount
      const total =
        data?.loanAccounts?.data?.reduce((sum, account) => {
          return sum + (parseFloat(account.principal) || 0);
        }, 0) || 0;
      setTotalDisbursed(total);
    },
    onError(error) {
      console.error("Error fetching loan accounts:", error);
    },
  });

  const stats = [
    {
      title: "Loan Categories",
      value: categoryCount,
      icon: IconCategory,
      color: "#FF6A00",
    },
    {
      title: "Loan Products",
      value: productCount,
      icon: IconFileDescription,
      color: "#0088FE",
    },
    {
      title: "Pending Applications",
      value: pendingApplications,
      icon: IconClock,
      color: "#FFA500",
    },
    {
      title: "Approved Applications",
      value: approvedApplications,
      icon: IconChecks,
      color: "#00C49F",
    },
    {
      title: "Active Loan Accounts",
      value: activeAccounts,
      icon: IconUsers,
      color: "#8884D8",
    },
    {
      title: "Total Disbursed",
      value: `ETB ${totalDisbursed.toLocaleString()}`,
      icon: IconCash,
      color: "#82ca9d",
    },
  ];

  const isLoading =
    categoriesLoading ||
    productsLoading ||
    pendingLoading ||
    approvedLoading ||
    accountsLoading;

  return (
    <div style={{ padding: "20px" }}>
      <Title order={2} mb="xl">
        Loan Provider Dashboard
      </Title>

      <SimpleGrid
        cols={3}
        spacing="lg"
        breakpoints={[
          { maxWidth: 980, cols: 2, spacing: "md" },
          { maxWidth: 755, cols: 1, spacing: "sm" },
        ]}
      >
        {stats.map((stat, index) => (
          <Card
            key={index}
            shadow="sm"
            p="lg"
            radius="md"
            className={classes.card}
          >
            <Group position="apart" mb="md">
              <stat.icon size={32} color={stat.color} stroke={1.5} />
            </Group>
            <Text size="xs" color="dimmed" weight={500} transform="uppercase">
              {stat.title}
            </Text>
            <Text size="xl" weight={700} mt="xs">
              {isLoading ? "..." : stat.value}
            </Text>
          </Card>
        ))}
      </SimpleGrid>

      <Grid mt="xl">
        <Grid.Col span={12}>
          <Card shadow="sm" p="lg" radius="md" className={classes.card}>
            <Group position="apart" mb="md">
              <Title order={3}>Welcome to Loan Provider Portal</Title>
              <Badge color="orange" size="lg">
                Active
              </Badge>
            </Group>
            <Text color="dimmed" size="sm" mb="md">
              Manage your loan categories, products, applications, and accounts
              from here. Use the sidebar navigation to access different
              sections.
            </Text>
            <SimpleGrid cols={2} spacing="md" mt="lg">
              <div>
                <Text weight={600} size="sm" mb="xs">
                  Quick Stats
                </Text>
                <Text size="xs" color="dimmed">
                  • {productCount} active loan products
                </Text>
                <Text size="xs" color="dimmed">
                  • {pendingApplications} applications awaiting review
                </Text>
                <Text size="xs" color="dimmed">
                  • {activeAccounts} active borrowers
                </Text>
              </div>
              <div>
                <Text weight={600} size="sm" mb="xs">
                  Recent Activity
                </Text>
                <Text size="xs" color="dimmed">
                  • {approvedApplications} total approved loans
                </Text>
                <Text size="xs" color="dimmed">
                  • ETB {totalDisbursed.toLocaleString()} total disbursed
                </Text>
                <Text size="xs" color="dimmed">
                  • Manage loan schedules and payments
                </Text>
              </div>
            </SimpleGrid>
          </Card>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default LoanProviderDashboard;
